/**
 * Authentication Service — Centralized JWT token management.
 *
 * Handles storing, retrieving, refreshing, and clearing JWT tokens
 * in localStorage. Replaces the session-cookie auth that Flask provided.
 *
 * Used by:
 *   - apiClient.js  (attaches Bearer token to every request)
 *   - LoginPage.js  (stores tokens after login/register)
 *   - AuthCallback.js (stores tokens from OAuth redirect)
 *   - ProtectedRoute.js (checks if user is authenticated)
 *   - Header.js (logout clears tokens)
 */

import { getBackendUrl } from '../utils/backendUrl';

const TOKEN_KEY = 'tv_access_token';
const REFRESH_TOKEN_KEY = 'tv_refresh_token';
const USER_KEY = 'tv_user';

// ── Token Storage ──────────────────────────────────────────────────────

/**
 * Store JWT tokens after successful login/register/OAuth.
 * @param {{ access_token: string, refresh_token: string }} tokens
 */
export const setTokens = (tokens) => {
  if (tokens.access_token) {
    localStorage.setItem(TOKEN_KEY, tokens.access_token);
  }
  if (tokens.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  }
};

/**
 * Get the current access token.
 * @returns {string|null}
 */
export const getAccessToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get the current refresh token.
 * @returns {string|null}
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Clear all auth data (tokens + cached user).
 * Called on logout or when tokens are invalid.
 */
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// ── User Cache ─────────────────────────────────────────────────────────

/**
 * Cache user data from /api/auth/status response.
 * Avoids re-fetching on every navigation.
 * @param {object} user
 */
export const setCachedUser = (user) => {
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_KEY);
  }
};

/**
 * Get cached user data.
 * @returns {object|null}
 */
export const getCachedUser = () => {
  try {
    const data = localStorage.getItem(USER_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

// ── Auth State Check ───────────────────────────────────────────────────

/**
 * Check if we have a stored access token (quick sync check).
 * Does NOT validate the token — use /api/auth/status for that.
 * @returns {boolean}
 */
export const hasToken = () => {
  return !!getAccessToken();
};

// ── Token Refresh ──────────────────────────────────────────────────────

let refreshPromise = null;

/**
 * Refresh the access token using the stored refresh token.
 * De-duplicates concurrent refresh calls (only one in-flight at a time).
 *
 * @returns {Promise<string|null>} New access token, or null if refresh failed.
 */
export const refreshAccessToken = async () => {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuth();
    return null;
  }

  refreshPromise = (async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        // Refresh token is expired/invalid — force re-login
        clearAuth();
        return null;
      }

      const data = await response.json();
      setTokens(data);
      return data.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearAuth();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ── Export all as default for convenience ───────────────────────────────

const authService = {
  setTokens,
  getAccessToken,
  getRefreshToken,
  clearAuth,
  setCachedUser,
  getCachedUser,
  hasToken,
  refreshAccessToken,
};

export default authService;



